/** @jsx tsx */
import { DNode } from "@dojo/framework/core/interfaces";
import { tsx } from "@dojo/framework/core/vdom";
import * as Constants from "../../Constants";
import * as _ from "lodash";
import { IScaleObjects } from "../../store/ApiContracts";
import {
	degToRad,
	displayTypeInstances,
	displayCustomView,
	getObjectCenter,
	getStartTypeForKey,
	radian,
	radToDeg,
	truncate,
	xor,
} from "../../store/reducers/util";
import * as selectionControls from "../factories/SelectionControls";
import { IGeoBoundaryMeta } from "../interfaces/model/dataStructures/GeoBoundaryMeta";
import { IExtents, IOffset } from "../interfaces/model/dataStructures/index";
import { IShapeGuide } from "../interfaces/model/geometry/ShapeGuide";
import { IXYAdjustHandle } from "../interfaces/model/geometry/XYAdjustHandle";
import { IShape, IGroupShape } from "../interfaces/model/PageObjects/index";
import { IPageObjectProperties, ITransform2D } from "../interfaces/model/properties/index";
import { PageObjectType } from "../interfaces/model/Types";
import { cancelCurrentOperation } from "../keyUtil";
import { IPosition } from "../meta/Drag/CsiDragController";
import { IDragResults } from "../meta/Drag/index";
import { getZoomValue } from "../selectors/index";
import { stateConstants } from "../selectors/index";
import { adjustPositionForRoot, getMaxDimension } from "../util";
import { DraggableWidget } from "./DraggableWidget";
import { AutosizeMode, CanvasMode } from "../interfaces/model/enumerations/index";

/** Selection box render properties */
export interface ISelectionBoxRenderProps {

	/** Parent canvas container */
	canvasContainerEl: HTMLElement;

	/** The pageObject for which selection to be shown */
	pageObject: PageObjectType;

	/** Flag indicating if canvas is panning */
	isPanning: boolean;

	/** If the selection box is over an element in a group, the group it is in */
	groupObject?: PageObjectType;

	/** Adjust handlers array, if any */
	adjustHandlers: IXYAdjustHandle[];

	/** Whether the page is a canvas template */
	isCanvasTemplate?: boolean;

	/** Flag to indicate if selection is participating in multi-selection */
	isMultiSelect: boolean;

	/** Flag to indicate whether or not the shape is temporary (for drawing) */
	isTempShape: boolean;

	/** Shape render-able geo boundary meta */
	geoBoundaryMeta: IGeoBoundaryMeta;

	/**
	 * Optional transform origin position. When available, the transformation will happen around this point.
	 * This is introduced to get ghost image for cropping tool positioned correctly with respect to shape.
	 * If omitted, "center" is used as transform origin
	 */
	transformOrigin?: IPosition;

	avList: IShapeGuide[];
	/**
	 * Path string for the shape. It is rendered in case of custom shape.
	 */
	path: string;
	/**
	 * selected object properties
	 */
	spPr: IPageObjectProperties<ITransform2D>;

	/**
	 * Flag to indicate that selection box is used for cropping
	 */
	forCropping: boolean;

	/**
	 * Flag to show rotation handle. Do not show if true, otherwise show
	 */
	noRotationHandle: boolean;

	/**
	 * Scale object callback
	 */
	scaleObjects: IScaleObjects;

	/**
	 * Rotate object callback
	 * @param rot rotation angle
	 * @param transient undo/redo stack flag.
	 */
	rotateObjects(rot: number, transient?: boolean): void;

	/**
	 * Select object callback
	 * @param selectedObject Object index in its parent child list
	 * @param isMultiSelect Flag to indicate multi selection
	 */
	selectObjectById(selectionId: string, isMultiSelect: boolean, transient: boolean, forceSingleSelect: boolean): void;

	/**
	 * Set canvas mode with the ability to preserve selection
	 * @param {CanvasMode} mode
	 * @param {boolean} preserveSelection
	 */
	setCanvasMode(mode: CanvasMode, preserveSelection?: boolean): void;

	/**
	 * Set Autosize callback
	 * @param autosize { AutosizeMode } - Autosize Mode
	 */
	setShapeAutosizeValues(autosize: AutosizeMode, resizeForContent: boolean): void;

	/**
	 * Set all aspect ratios callback
	 */
	setAllAspectRatios(transient: boolean): void;

	/**  Set shape adjust values
	 * @param dx Delta movement along x axis
	 * @param dy Delta movement along y axis
	 * @param adjustHandler The source adjust handler for shape adjust
	 * @param avList Shape adjust values
	 */
	setShapeAdjustValues(dx: number, dy: number, adjustHandler: IXYAdjustHandle,
		avList: IShapeGuide[], refExt: IExtents, transient: boolean): void;
}

/**
 * Any Object interface
 */
export interface IAnyObjectType {
	[key: string]: any;
}

/**
 * Selection Box.
 * Provides base component for rendering object selection box
 */
export class SelectionBox extends DraggableWidget<ISelectionBoxRenderProps> {
	/**
	 * Holds radian for selected object
	 */
	private radians: number = 0;

	/**
	 * Stores original rotation angle for object
	 */
	private originalRot: number = 0;

	/**
	 * Rotate object on rotate handler drag
	 * @param dragResult { IDragResults } Drag result
	 */
	protected onDrag(dragResult: IDragResults, isComplete: boolean) {
		const isEscapeKeyPress = cancelCurrentOperation(dragResult.keyData);
		const properties = this.properties;
		if ((dragResult.delta.x || dragResult.delta.y) && !isComplete) {
			// Get the center of the selected object.  If it is in a group, get its absolute page position.
			let centerPt: IOffset;
			if (properties.groupObject) {
				centerPt = getObjectCenter(properties.pageObject.spPr, properties.groupObject.spPr);
			} else {
				centerPt = getObjectCenter(properties.pageObject.spPr);
			}

			adjustPositionForRoot(properties.canvasContainerEl, dragResult.positionInCanvas);

			// Get radian between two points
			let r = radian(centerPt, dragResult.positionInCanvas as IOffset);

			// Account for group rotation.
			if (properties.groupObject) {
				r = r - degToRad(properties.groupObject.spPr.xfrm.rot);

				if (properties.groupObject.spPr.xfrm.flipH) {
					r = -r;
				}
			}

			// Rotate shape to 15 degrees if Shift key held
			if (dragResult.keyData && dragResult.keyData.shiftKey) {
				r = Math.round(r / Constants.ROTATE_GRID_ANGLE) * Constants.ROTATE_GRID_ANGLE;
			}
			r -= this.radians;
			this.radians += r;

			if (!r) { return; };
			// Convert radian angle to degree
			let d = radToDeg(r) % 360;
			// Truncate number to precision 1
			d = truncate(d, 1);
			properties.rotateObjects(d, true);
		} else {
			const finalRot = isEscapeKeyPress ?
				(this.originalRot - properties.pageObject.spPr.xfrm.rot) : 0;
			properties.rotateObjects(finalRot, isEscapeKeyPress);
		}
	}

	/**
	 * Pointer down event for rotation handler
	 * @param event { PointerEvent }
	 */
	protected onRotateCirclePointerDown(event: PointerEvent) {
		this.radians = degToRad(this.properties.pageObject.spPr.xfrm.rot);
		this.originalRot = this.properties.pageObject.spPr.xfrm.rot;
	}

	/**
	 * Pointer down event for attribute button
	 * @param event { PointerEvent }
	 */
	protected onAttributeButtonPointerDown(event: PointerEvent) {
		(window as any).blockNextPointerUp = true;
		
		const {
			pageObject,
			selectObjectById,
			setCanvasMode,
		} = this.properties;
		
		if (pageObject.isAttributePlaceholder) {
			selectObjectById(pageObject.shapeId, false, true, true);
			const groupObject = pageObject as IGroupShape;
			const firstObjectFromGroup = groupObject.childObjects[0] as IShape;
			const type = getStartTypeForKey(firstObjectFromGroup.attributeKey) as string;

			// show type instances and put canvas in type instance drag mode
			setCanvasMode(CanvasMode.DRAG_TYPE_INSTANCE, true);
			displayTypeInstances(type);
		}
	}

	/**
	 * Pointer down event for edit attribute button
	 * @param event { PointerEvent }
	 */
	protected onEditAttributeButtonPointerDown(event: PointerEvent) {
		const groupObject = this.properties.pageObject as any;
		const attributeKeys = _.map(groupObject.childObjects, (childObject) => childObject.attributeKey);
		// select this only this attribute placeholder so nothing else is deleted
		this.properties.selectObjectById(this.properties.pageObject.shapeId, false, true, true);
		// pass in the old groupObject to model the new one after
		displayCustomView(attributeKeys!, groupObject);
	}

	/**
	 * Override WidgetBase#render to produce a virtual DOM tree.
	 * @returns {HNode} Each time render() executes, it should build the entire virtual DOM tree.
	 */
	protected render() {
		// While performing undo operation if shape is selected and removed in undo operation
		// then here getting pageObject as undefined so returning null this will prevent render to execute.
		if (!this.properties.pageObject) {
			return null;
		}
		let { spPr } = this.properties.pageObject as IShape;
		spPr = _.cloneDeep(spPr);
		const height = getMaxDimension(spPr.xfrm.ext.cy);
		const width = getMaxDimension(spPr.xfrm.ext.cx);

		const { geoBoundaryMeta } = this.properties;
		const svgHeight = getMaxDimension(geoBoundaryMeta.height);
		const svgWidth = getMaxDimension(geoBoundaryMeta.width);

		let offX = spPr.xfrm.off.x;
		let offY = spPr.xfrm.off.y;

		let { rot, flipH } = spPr.xfrm;
		const groupFlipH = this.properties.groupObject ? this.properties.groupObject.spPr.xfrm.flipH : false;
		flipH = xor(flipH, groupFlipH);

		let scale = "";
		if (flipH) {
			scale = "scale(-1, 1)";
		}
		rot = rot || 0;
		if (groupFlipH) {
			rot = rot * -1;
		}

		const rotatedPoint = (cx: number, cy: number, x: number, y: number, angle: number) => {
			const radians = (Math.PI / 180) * angle;
			const cos = Math.cos(radians);
			const sin = Math.sin(radians);
			const nx = (cos * (x - cx)) + (sin * (y - cy)) + cx;
			const ny = (cos * (y - cy)) - (sin * (x - cx)) + cy;

			return { nx, ny };
		};

		if (this.properties.groupObject !== undefined) {
			if (groupFlipH) {
				offX = this.properties.groupObject.spPr.xfrm.ext.cx - spPr.xfrm.off.x - spPr.xfrm.ext.cx;
			}

			const cx = this.properties.groupObject.spPr.xfrm.ext.cx / 2;
			const cy = this.properties.groupObject.spPr.xfrm.ext.cy / 2;

			const objCx = spPr.xfrm.ext.cx / 2;
			const objCy = spPr.xfrm.ext.cy / 2;

			const groupRot = this.properties.groupObject.spPr.xfrm.rot;

			rot += groupRot;

			// Calculate the position of the shape post-rotation
			const pointsObj = rotatedPoint(cx, cy, offX, offY, -groupRot);

			const newTopLeft = rotatedPoint(objCx, objCy, 0, 0, -groupRot);

			offX = this.properties.groupObject.spPr.xfrm.off.x + pointsObj.nx - newTopLeft.nx;
			offY = this.properties.groupObject.spPr.xfrm.off.y + pointsObj.ny - newTopLeft.ny;
		}

		const locked: boolean = spPr.locked || false;
		let strokeColor: string = locked ? "red" : "blue";
		strokeColor = this.properties.forCropping ? "black" : strokeColor;

		let resizeHandler;
		let adjustHandlers;

		if (this.properties.forCropping) {
			resizeHandler = selectionControls.cropping(
				this.properties.pageObject,
				this.properties.scaleObjects,
				this.properties.setAllAspectRatios,
			);
		} else {
			// standard page object selection
			resizeHandler = selectionControls.standard(
				this.properties.canvasContainerEl,
				this.properties.isPanning,
				this.properties.setShapeAutosizeValues,
				this.properties.pageObject,
				this.properties.scaleObjects,
				this.properties.setAllAspectRatios,
				this.properties.isTempShape,
				this.properties.groupObject,
				this.properties.transformOrigin,
			);
		}

		if (this.properties.adjustHandlers && !this.properties.isMultiSelect) {
			adjustHandlers = selectionControls.adjust(
				this.properties.adjustHandlers,
				this.properties.avList,
				this.properties.canvasContainerEl,
				this.properties.pageObject,
				this.properties.setShapeAdjustValues,
				this.properties.geoBoundaryMeta,
			);
		}

		const transformOrigin = this.properties.transformOrigin ?
			`${this.properties.transformOrigin.x}px ${this.properties.transformOrigin.y}px` : "center";

		const svgStyleTop = geoBoundaryMeta.yMin ?
			Math.min(-Constants.ADDITIONAL_HEIGHT, geoBoundaryMeta.yMin) : -Constants.ADDITIONAL_HEIGHT;
		const svgStyleLeft = geoBoundaryMeta.xMin ?
			Math.min(-Constants.ADDITIONAL_WIDTH, geoBoundaryMeta.xMin) : -Constants.ADDITIONAL_WIDTH;

		const svgStyleTranslateX = geoBoundaryMeta.xMin ?
			Math.max(Constants.ADDITIONAL_WIDTH, -geoBoundaryMeta.xMin) : Constants.ADDITIONAL_WIDTH;
		const svgStyleTranslateY = geoBoundaryMeta.yMin ?
			Math.max(Constants.ADDITIONAL_HEIGHT, -geoBoundaryMeta.yMin) : Constants.ADDITIONAL_HEIGHT;
		return (
			<div
				key={`pageObject_${this.properties.key}`}
				styles={{
					display: "inline-block",
					left: `${offX}px`,
					position: "absolute",
					top: `${offY}px`,
					zIndex: `${Constants.MAX_Z_INDEX}`,
				}}
			>
				<div
					styles={{
						height: `${height}px`,
						transform: `rotate(${rot}deg) ${scale}`,
						transformOrigin: `${transformOrigin}`,
						width: `${width}px`,
					}}
				>
					{this.renderAttributeButton(width, height, locked)}
					{this.renderLockIcon(locked, strokeColor)}
					<svg
						height={`${Math.ceil(svgHeight) + Constants.ADDITIONAL_HEIGHT + Constants.ADDITIONAL_DIMENSION_FACTOR}px`}
						styles={{
							overflow: "visible",
							top: `${svgStyleTop}px`,
							left: `${svgStyleLeft}px`,
							position: "absolute",
						}}
						/**
						 * Reference: C8-6740 - to avoid caterpillar like effect while moving shape,
						 * additional dimension factor is being added to SVG element's width and height.
						 * Assigning -7px to left & 7px to padding left and -20px to top & 20px to
						 * padding top attributes for adjusting the position of the SVG element so that
						 * selection box will be rendered at correct position.
						 * To avoid cut off the resize handlers (Reference: C8-6739)  the values of
						 * above attributes extends to 7px and 20px here instead of 2px in the page
						 * object's SVG element.
						 */
						width={`${Math.ceil(svgWidth) + Constants.ADDITIONAL_WIDTH + Constants.ADDITIONAL_DIMENSION_FACTOR}px`}
						x="0"
						y="0"
					>
						<g transform={`translate(${svgStyleTranslateX}, ${svgStyleTranslateY})`}>
							{this.renderBoundingBox(strokeColor)}
							{this.renderRotationCircle(getMaxDimension(spPr.xfrm.ext.cx), strokeColor)}
							{resizeHandler}
							{adjustHandlers}
						</g>
					</svg>
				</div>
			</div>
		);
	}

	/**
	 * Draw point and line for rotation handle
	 * @param width {number}
	 * @returns {DNode} Rotation handler for selected object
	 *
	 * FIXME: Move rotation circle control to its own widget module
	 */
	private renderRotationCircle(width: number, strokeColor: string): DNode {
		// when EDIT_PICTURE mode is activated, standard selection control are for image under crop activity.
		// In this case, we do not want to rotate image, so not showing rotation handle.
		if (this.properties.noRotationHandle) {
			return null;
		}
		const x1 = width / 2;
		const y1 = - Constants.ROTATE_HANDLER_LOCATION;
		// call draggable meta
		this.requestDragMeta("rotationCircle");
		return (
			<g>
				<line
					fill="none"
					stroke={`${strokeColor}`}
					stroke-width={`${getZoomValue(Constants.SELECTION_STROKE_WIDTH)}`}
					x1={`${x1}`}
					x2={`${x1}`}
					y1="0"
					y2={`${y1}`}
				/>
				<circle
					classes="c8_canvas_userSelectNone"
					cx={`${x1}`}
					cy={`${y1}`}
					fill="white"
					key="rotationCircle"
					onpointerdown={this.onRotateCirclePointerDown}
					pointer-events="visible"
					r={`${Constants.CIRCULAR_HANDLE_RADIUS}`}
					stroke={`${strokeColor}`}
					stroke-width={`${getZoomValue(Constants.SELECTION_HANDLER_STROKE_WIDTH)}`}
				/>
			</g>
		);
	}

	/**
	 * Draw a plus button and give it function on click
	 * @param width {number}
	 * @returns {DNode} plus button with attached function
	 */
	private renderAttributeButton(width: number, height: number, locked: boolean): DNode {
		const spPr = this.properties.pageObject;
		let usesContextType = false;
		const constants = stateConstants();

		if (spPr.isAttributePlaceholder) {
			const groupObject = this.properties.pageObject as IGroupShape;
			const typeKey = getStartTypeForKey((groupObject.childObjects[0] as IShape).attributeKey!);

			usesContextType = typeKey === constants.contextType;
		}

		if (!spPr.isAttributePlaceholder || locked) {
			return;
		}

		const left = width - Constants.ATTRIBUTE_BUTTON_LEFT_OFFSET;
		const top = height - Constants.ATTRIBUTE_BUTTON_TOP_OFFSET;
		const path = `M12,2C6.47,2 2,6.47 2,12C2,17.53 6.47,22 12,22C17.53,22 22,17.53
		 22,12C22,6.47 17.53,2 12,2M15.1,7.07C15.24,7.07 15.38,7.12 15.5,7.23L16.77,8.5C17,8.72 17,9.07
		  16.77,9.28L15.77,10.28L13.72,8.23L14.72,7.23C14.82,7.12
		   14.96,7.07 15.1,7.07M13.13,8.81L15.19,10.87L9.13,16.93H7.07V14.87L13.13,8.81Z`;
		if (this.properties.isCanvasTemplate) {
			return (
				<div
					classes="c8_canvas_attribute_button"
					key="add_attribute_button"
					onpointerdown={this.onEditAttributeButtonPointerDown}
					pointer-events="visible"
					styles={{
						position: "absolute",
						top: `${top}px`,
						left: `${left}px`,
					}}
				>
					<svg styles={{ width:"15px", height: "15px" }} viewBox="0 0 24 24">
						<path d={`${path}`} fill="#4477bb" />
					</svg>
				</div>
			);
		} else if (!usesContextType) {
			return (
				<span
					classes={["c8_canvas_attribute_button", "material-icons"]}
					key="add_attribute_button"
					onpointerdown={this.onAttributeButtonPointerDown}
					pointer-events="visible"
					styles={{ position: "absolute", top: `${top}px`, left: ` ${left}px` }}
				>
					add_circle
				</span>
			);
		}
	}

	/**
	 * Draw rectangle for bounding box
	 * @param {string} strokeColor to be used
	 * @returns {DNode} Bounding box for selected object
	 */
	private renderBoundingBox(strokeColor: string): DNode {
		return (
			<rect
				fill="none"
				height={`${this.properties.pageObject.spPr.xfrm.ext.cy}px`}
				pointer-events="none"
				stroke={`${strokeColor}`}
				stroke-width={`${getZoomValue(Constants.SELECTION_STROKE_WIDTH)}`}
				width={`${this.properties.pageObject.spPr.xfrm.ext.cx}px`}
				x="0"
				y="0"
			/>
		);
	}

	private renderLockIcon(locked: boolean, strokeColor: string): DNode {
		if (locked) {
			return (
				<span
					classes={["material-icons", "csi-icon-color-red", "c8_canvas_shape_lock_icon"]}
					styles={{ fontSize: "9px" }}
				>lock</span>
			);
		} else {
			return null;
		}
	}
}

export default SelectionBox;
